import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const api = axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  api.interceptors.request.use((config) => {
    const currentToken = localStorage.getItem('token');
    if (currentToken) {
      config.headers.Authorization = `Bearer ${currentToken}`;
    }
    return config;
  });

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, refresh_token } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('refreshToken', refresh_token);
    setToken(access_token);
    
    // Fetch user info
    const userResponse = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${access_token}` }
    });
    setUser(userResponse.data);
    return userResponse.data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    setToken(null);
    setUser(null);
  }, []);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, api }}>
      {children}
    </AuthContext.Provider>
  );
};

// API Service
export const apiService = {
  // Auth
  login: (email, password) => axios.post(`${API}/auth/login`, { email, password }),
  register: (data) => axios.post(`${API}/auth/register`, data),
  seedDemo: () => axios.post(`${API}/seed-demo`),
  
  // Users Management
  getUsers: (token) => axios.get(`${API}/v1/users`, { headers: { Authorization: `Bearer ${token}` } }),
  inviteUser: (data, token) => axios.post(`${API}/v1/users/invite`, data, { headers: { Authorization: `Bearer ${token}` } }),
  
  // Tenants
  getTenants: (token) => axios.get(`${API}/v1/tenants`, { headers: { Authorization: `Bearer ${token}` } }),
  createTenant: (data) => axios.post(`${API}/v1/tenants`, data),
  
  // Connectors
  getConnectors: (token) => axios.get(`${API}/v1/connectors`, { headers: { Authorization: `Bearer ${token}` } }),
  createConnector: (data, token) => axios.post(`${API}/v1/connectors`, data, { headers: { Authorization: `Bearer ${token}` } }),
  updateConnector: (id, data, token) => axios.put(`${API}/v1/connectors/${id}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  deleteConnector: (id, token) => axios.delete(`${API}/v1/connectors/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
  testConnector: (id, token) => axios.post(`${API}/v1/connectors/${id}/test`, {}, { headers: { Authorization: `Bearer ${token}` } }),
  introspectConnector: (id, token) => axios.get(`${API}/v1/connectors/${id}/introspect`, { headers: { Authorization: `Bearer ${token}` } }),
  getConnectorTables: (id, token) => axios.get(`${API}/v1/connectors/${id}/tables`, { headers: { Authorization: `Bearer ${token}` } }),
  updateConnectorTables: (id, selectedTables, token) => axios.put(`${API}/v1/connectors/${id}/tables`, { selected_tables: selectedTables }, { headers: { Authorization: `Bearer ${token}` } }),
  
  // Extractions
  getExtractions: (token) => axios.get(`${API}/v1/extractions`, { headers: { Authorization: `Bearer ${token}` } }),
  getExtraction: (id, token) => axios.get(`${API}/v1/extractions/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
  createExtraction: (data, token) => axios.post(`${API}/v1/extractions`, data, { headers: { Authorization: `Bearer ${token}` } }),
  updateExtraction: (id, data, token) => axios.put(`${API}/v1/extractions/${id}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  deleteExtraction: (id, token) => axios.delete(`${API}/v1/extractions/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
  compileExtraction: (id, token) => axios.post(`${API}/v1/extractions/${id}/compile`, {}, { headers: { Authorization: `Bearer ${token}` } }),
  previewExtraction: (id, token) => axios.post(`${API}/v1/extractions/${id}/preview`, {}, { headers: { Authorization: `Bearer ${token}` } }),
  runExtraction: (id, filters, token) => axios.post(`${API}/v1/extractions/${id}/run`, filters, { headers: { Authorization: `Bearer ${token}` } }),
  
  // Dashboards
  getDashboards: (token) => axios.get(`${API}/v1/dashboards`, { headers: { Authorization: `Bearer ${token}` } }),
  getDashboard: (id, token) => axios.get(`${API}/v1/dashboards/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
  createDashboard: (data, token) => axios.post(`${API}/v1/dashboards`, data, { headers: { Authorization: `Bearer ${token}` } }),
  updateDashboard: (id, data, token) => axios.put(`${API}/v1/dashboards/${id}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  deleteDashboard: (id, token) => axios.delete(`${API}/v1/dashboards/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
  renderDashboard: (id, filters, token) => axios.post(`${API}/v1/dashboards/${id}/render`, filters, { headers: { Authorization: `Bearer ${token}` } }),
  
  // Widgets
  getWidgets: (dashboardId, token) => axios.get(`${API}/v1/dashboards/${dashboardId}/widgets`, { headers: { Authorization: `Bearer ${token}` } }),
  createWidget: (dashboardId, data, token) => axios.post(`${API}/v1/dashboards/${dashboardId}/widgets`, data, { headers: { Authorization: `Bearer ${token}` } }),
  updateWidget: (id, data, token) => axios.put(`${API}/v1/widgets/${id}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  deleteWidget: (id, token) => axios.delete(`${API}/v1/widgets/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
  
  // Templates
  getTemplates: (type, token) => axios.get(`${API}/v1/templates${type ? `?type=${type}` : ''}`, { headers: { Authorization: `Bearer ${token}` } }),
  getTemplate: (id, token) => axios.get(`${API}/v1/templates/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
  createTemplate: (data, token) => axios.post(`${API}/v1/templates`, data, { headers: { Authorization: `Bearer ${token}` } }),
  deleteTemplate: (id, token) => axios.delete(`${API}/v1/templates/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
  applyTemplate: (id, newName, token) => axios.post(`${API}/v1/templates/${id}/apply`, { new_name: newName }, { headers: { Authorization: `Bearer ${token}` } }),
  
  // AI WILL
  askWill: (connectorId, message, context, token) => axios.post(`${API}/v1/ai/will`, {
    connector_id: connectorId,
    message,
    context
  }, { headers: { Authorization: `Bearer ${token}` } }),
};

export default apiService;
